/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The Settings sections this user can see: the cards on the Settings page
 * and, in v2, the items in the settings menu (SettingsLayoutV2). One list, so
 * a section hidden for non-admins or locked behind an add-on is hidden or
 * locked in both places the same way. Moved here unchanged from Settings.js.
 */

import {
  Person,
  Business,
  Support,
  Security,
  Notifications,
  Language,
  WebAsset,
  Extension,
  AutoFixHigh,
  Widgets,
  FlightTakeoff,
  AutoAwesome,
  Key,
} from '@mui/icons-material';
import ViewListIcon from '@mui/icons-material/ViewList';
import PaymentIcon from '@mui/icons-material/Payment';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import WorkspacePremium from '@mui/icons-material/WorkspacePremium';
import EmailIcon from '@mui/icons-material/Email';
import AppsIcon from '@mui/icons-material/Apps';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { useAuth } from 'auth/Auth';
import SeepLogo from 'components/SeepLogo';
import UserSettings from 'components/SettingsComponents/UserManagementSettings';
import SupportSettings from 'components/SettingsComponents/SupportSettings';
import WebFormsSettings from 'components/SettingsComponents/WebFormsSettings';
import PageLayoutSetting from 'components/SettingsComponents/PagelayoutSetting';
import SidebarSettings from 'components/SettingsComponents/SidebarSettings';
import TravelSettings from 'components/SettingsComponents/TravelSettings';
import { useModuleEntitlements } from 'hooks/useModuleEntitlements';
import { useFeatureLicense } from 'hooks/useFeatureLicense';

export default function useSettingsSections() {
  const { user } = useAuth();
  const {
    hasTravelEntitlement,
    hasEdition,
    isModuleEnabled,
    loading: entitlementsLoading,
  } = useModuleEntitlements();
  const { features } = useFeatureLicense();

  // Call Center is a purchased add-on. Until the entitlement snapshot has
  // arrived the hook reports nothing enabled, so locking on it here would
  // flash a lock on every tenant that does own the module.
  const callCenterLocked =
    !entitlementsLoading && !isModuleEnabled('CALL_CENTER')
      ? 'Call Center add-on required'
      : null;

  // SEEP AI is a usage-billed add-on; same no-flash rule as Call Center.
  const seepLocked =
    !entitlementsLoading && !isModuleEnabled('SEEP_AI')
      ? 'SEEP AI add-on required'
      : null;

  // AI Assistant: seat-licensed add-on, same no-flash rule.
  const aiAssistantLocked =
    !entitlementsLoading && !isModuleEnabled('AI_ASSISTANT')
      ? 'AI Assistant add-on required'
      : null;
  const apiAccessLocked =
    !entitlementsLoading && !isModuleEnabled('API_ACCESS')
      ? 'API Access add-on required'
      : null;

  const hasWebsiteEntitlement =
    hasTravelEntitlement || (hasEdition('REAL_ESTATE') && isModuleEnabled('RE_WEBSITE'));

  // Settings that are available in playground mode
  const playgroundAvailableSettings = [
    'Company Settings',
    'Automation Builder',
    'Page Layouts',
    'Sidebar Navigation',
    'Integrations'
  ];

  // Check if setting is available in playground mode
  const isAvailableInPlayground = (settingName) => {
    return playgroundAvailableSettings.includes(settingName);
  };

  const isSystemAdmin =
    user?.role === 'system_admin' || user?.role === 'SYSTEM_ADMIN';

  // Define settings sections based on user role and entitlements
  const settingsSections = [
    //   --------------------------------------------
    //   This is old implementation where company settings
    //   opens up in a popup, nothing has been changes as a functional
    //   point of view, just implemented full page configuration.
    //   Once confirmed that the new implementation is running smoothly
    //   this part can be completely removed along with all the linked files.
    //   WORRIED ABOUT - GeneralSettings ? Component, it is exactly cloned in the
    //   new implementation given below.
    //   --------------------------------------------
    // {
    //   title: "Old Company Settings",
    //   icon: <Person />,
    //   component: <GeneralSettings />,
    //   description: "Manage your Company settings and preferences",
    //   availableInPlayground: true,
    //   urlTab: 'old_company'
    // },
    {
      title: "Company Settings",
      icon: <Person />,
      component: null,
      description: "Manage your Company settings and preferences",
      isNavigate: true,
      navigateTo: "/settings/company-settings",
      availableInPlayground: false,
      urlTab: 'company-settings'
    },
    ...(isSystemAdmin ? [{
      title: "User Management",
      icon: <Business />,
      component: <UserSettings />,
      description: "Manage users, roles and permissions",
      availableInPlayground: false,
      urlTab: 'users'
    }] : []),
    ...(isSystemAdmin ? [{
      title: "Roles & Permissions",
      icon: <Business />,
      component: null,
      description: "Define roles and what each role can see and do",
      isNavigate: true,
      navigateTo: "/settings/roles",
      availableInPlayground: false,
      urlTab: 'roles'
    }] : []),
    ...(isSystemAdmin ? [{
      title: "Teams",
      icon: <Business />,
      component: null,
      description: "Group people so roles can grant access to a team's records",
      isNavigate: true,
      navigateTo: "/settings/teams",
      availableInPlayground: false,
      urlTab: 'teams'
    }] : []),
    ...(isSystemAdmin ? [{
      title: "Automation Builder",
      icon: <AutoFixHigh />,
      component: null,
      description: "Create and manage automated workflows for your CRM",
      isNavigate: true,
      navigateTo: "/automations",
      availableInPlayground: true,
      urlTab: 'automation'
    }] : []),
    ...(isSystemAdmin && features.custom_objects ? [{
      title: "Custom Objects",
      icon: <Widgets />,
      component: null,
      description: "Design custom modules and CMS-style content types",
      isNavigate: true,
      navigateTo: "/settings/custom-objects",
      availableInPlayground: false,
      urlTab: 'custom-objects'
    }] : []),
    {
      title: "Page Layouts",
      icon: <WebAsset />,
      component: <PageLayoutSetting />,
      description: "View and manage page layouts",
      availableInPlayground: true,
      urlTab: 'layouts'
    },
    {
      title: "Sidebar Navigation",
      icon: <ViewListIcon />,
      component: <SidebarSettings />,
      description: "Choose which modules appear in your sidebar and add custom sections",
      availableInPlayground: true,
      urlTab: 'sidebar'
    },
    {
      title: "Integrations",
      icon: <Extension />,
      component: null,
      description: "Configure third-party integrations and API keys",
      isNavigate: true,
      navigateTo: "/settings/integrations",
      availableInPlayground: true,
      urlTab: 'integrations'
    },
    ...(hasTravelEntitlement ? [{
      title: "Travel",
      icon: <FlightTakeoff />,
      component: <TravelSettings />,
      description: "Manage travel related settings, trips and publishers",
      isNavigate: false,
      navigateTo: null,
      availableInPlayground: true,
      urlTab: 'travel',
      isTravel: true
    }] : []),
    // Travel sites need TRAVEL; real estate sites need the RE_WEBSITE add-on
    // and the Real Estate edition (the same gate as /api/v1/websites).
    ...(hasWebsiteEntitlement ? [{
      title: "Website",
      icon: <WebAsset />,
      component: null,
      description: hasTravelEntitlement
        ? "Configure your travel website, domain, layout, catalogue and checkout integrations"
        : "Configure your property website, domain, layout and the listings it shows",
      isNavigate: true,
      navigateTo: "/website",
      availableInPlayground: false,
      urlTab: 'website',
    }] : []),
    // Only for tenants holding the Real Estate edition (any RE module).
    ...(hasEdition('REAL_ESTATE') ? [{
      title: "Real Estate",
      icon: <ApartmentIcon />,
      component: null,
      description: "Choose the kinds of real estate business you run; see the country rules you follow",
      isNavigate: true,
      navigateTo: "/settings/real-estate",
      availableInPlayground: false,
      urlTab: 'real-estate',
    }] : []),
    ...(isSystemAdmin && hasTravelEntitlement ? [{
      title: "Payments",
      icon: <PaymentIcon />,
      component: null,
      description: "Connect your Stripe or Razorpay account for travel checkout",
      isNavigate: true,
      navigateTo: "/settings/payments",
      availableInPlayground: false,
      urlTab: 'payments'
    }] : []),
    ...(isSystemAdmin ? [{
      title: "Telephony",
      icon: <SettingsPhoneIcon />,
      component: null,
      description: "Connect Twilio, map phone numbers, phone menus and call recording",
      isNavigate: true,
      navigateTo: "/settings/telephony",
      availableInPlayground: false,
      urlTab: 'telephony',
      lockedReason: callCenterLocked
    }] : []),
    ...(isSystemAdmin ? [{
      title: "SEEP AI",
      icon: <SeepLogo size={46} framed />,
      component: null,
      description: "Voice assistant for your website and phone lines, in your customers' languages",
      isNavigate: true,
      navigateTo: "/settings/seep-ai",
      availableInPlayground: false,
      urlTab: 'seep-ai',
      lockedReason: seepLocked
    }] : []),
    ...(isSystemAdmin ? [{
      title: "AI Assistant",
      icon: <AutoAwesome />,
      component: null,
      description: "Give users AI Assistant seats and see how your credits are used",
      isNavigate: true,
      navigateTo: "/settings/ai-assistant",
      availableInPlayground: false,
      urlTab: 'ai-assistant',
      lockedReason: aiAssistantLocked
    }] : []),
    {
      title: "Connected apps",
      icon: <AppsIcon />,
      component: null,
      description: "Apps you have allowed to use PearlCRM as you",
      isNavigate: true,
      navigateTo: "/settings/connected-apps",
      availableInPlayground: false,
      urlTab: 'connected-apps'
    },
    ...(isSystemAdmin ? [{
      title: "Developer & API",
      icon: <Key />,
      component: null,
      description: "OAuth clients for integrations that call the PearlCRM API",
      isNavigate: true,
      navigateTo: "/settings/developer",
      availableInPlayground: false,
      urlTab: 'developer',
      lockedReason: apiAccessLocked
    }] : []),
    // Not gated on CALL_CENTER: a queue can hold cases and leads for a tenant
    // that owns no phone number, so hiding this behind telephony would hide
    // the feature from the people most likely to want it.
    ...(isSystemAdmin ? [{
      title: "Queues",
      icon: <MoveToInboxIcon />,
      component: null,
      description: "Route cases, leads and calls to the right people, with capacity limits",
      isNavigate: true,
      navigateTo: "/settings/queues",
      availableInPlayground: false,
      urlTab: 'queues'
    }] : []),
    // Beside Queues rather than beside Teams: the only thing a skill does is
    // decide who a queue reaches, so it is useless to anyone not on this page.
    ...(isSystemAdmin ? [{
      title: "Skills",
      icon: <WorkspacePremium />,
      component: null,
      description: "Tag people with what they can handle, so queues can route by it",
      isNavigate: true,
      navigateTo: "/settings/skills",
      availableInPlayground: false,
      urlTab: 'skills'
    }] : []),
    {
      title: "Support Access",
      icon: <Support />,
      component: <SupportSettings />,
      description: "Manage support team access",
      availableInPlayground: false,
      urlTab: 'support'
    },
    {
      title: "Web Forms",
      icon: <WebAsset />,
      component: <WebFormsSettings />,
      description: "Configure Web-to-Lead and Web-to-Case forms",
      availableInPlayground: false,
      urlTab: 'webforms'
    },
    ...(isSystemAdmin ? [{
      title: "Email to Case",
      icon: <EmailIcon />,
      component: null,
      description: "Connect your Brevo account to create cases from support email",
      isNavigate: true,
      navigateTo: "/settings/email-to-case",
      availableInPlayground: false,
      urlTab: 'email-to-case'
    }] : []),
  ];

  // Additional sections that could be added in the future
  const futureSections = [
    {
      title: "Security",
      icon: <Security />,
      description: "Configure security settings and 2FA",
      comingSoon: true
    },
    {
      title: "Notifications",
      icon: <Notifications />,
      description: "Manage notification preferences",
      comingSoon: true
    },
    {
      title: "Language & Region",
      icon: <Language />,
      description: "Set your preferred language and region",
      comingSoon: true
    }
  ];

  return { settingsSections, futureSections, isAvailableInPlayground, isSystemAdmin };
}
